import dayjs from "dayjs";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { Navigate, Outlet } from "react-router-dom";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { format, isToday, isYesterday, parseISO } from "date-fns";
import { dialCodeToISO } from "components/Fileds/DialToIso";
import { useGetSettingsQuery } from "pages/settings/ManageSettings/services";
dayjs.extend(customParseFormat);

export const messageNotification = (
  text,
  type,
  autoClose = 5000,
  position = "top-right"
) => {
  toast[type](text, {
    position: position,
    autoClose: autoClose,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });
};

export const ProtectedRoutes = () => {
  const posData = useSelector((state) => state?.posData);
  const token = posData?.token;
  const isProfileCompleted = posData?.vendor?.is_profile_completed;

  if (!token) {
    return <Navigate to="/login" />;
  }

  if (isProfileCompleted === false || isProfileCompleted === 0) {
    return <Navigate to="/onboarding" />;
  }

  return <Outlet />;
}

export const ProtectedRoutesforLoggedIn = () => {
  const posData = useSelector((state) => state?.posData);
  const token = posData?.token;
  const isProfileCompleted = posData?.vendor?.is_profile_completed;

  if (!token) {
    return <Outlet />;
  }

  if (isProfileCompleted === false || isProfileCompleted === 0) {
    return <Navigate to="/onboarding" />;
  }

  return <Navigate to="/" />;
}

export const OnboardingRoute = () => {
  const posData = useSelector((state) => state?.posData);
  const token = posData?.token;
  const isProfileCompleted = posData?.vendor?.is_profile_completed;

  if (!token) {
    return <Navigate to="/login" />;
  }

  if (isProfileCompleted === true || isProfileCompleted === 1) {
    return <Navigate to="/" />;
  }

  return <Outlet />;
}

export const getOrderTypePermissions = (posData) => {
  const raw = posData?.vendor?.order_type_permissions ?? posData?.order_type_permissions;
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      let parsed = JSON.parse(raw);
      if (typeof parsed === "string") {
        try {
          parsed = JSON.parse(parsed);
        } catch {
          // ignore double parse error
        }
      }
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (e) {
      console.error("Error parsing order_type_permissions:", e);
      return {};
    }
  }
  if (typeof raw === "object") {
    return raw;
  }
  return {};
};

export const isPermissionAllowed = (permissions, key) => {
  if (!permissions) {
    return true;
  }

  // Handle phone order aliases
  if (
    key === "permission_phone_order" ||
    key === "permission_pos_phone_order" ||
    key === "permisssion_pos_phone_order" ||
    key === "phone_order"
  ) {
    const keys = [
      "permission_phone_order",
      "permission_pos_phone_order",
      "permisssion_pos_phone_order",
      "phone_order",
    ];
    for (const k of keys) {
      if (permissions[k] !== undefined && permissions[k] !== null) {
        const val = permissions[k];
        if (val === 0 || val === "0" || val === false) {
          return false;
        }
      }
    }
    return true;
  }

  // Handle calendar view aliases
  if (key === "permission_pos_calender_view" || key === "permission_pos_calendar_view") {
    const keys = ["permission_pos_calender_view", "permission_pos_calendar_view"];
    for (const k of keys) {
      if (permissions[k] !== undefined && permissions[k] !== null) {
        const val = permissions[k];
        if (val === 0 || val === "0" || val === false) {
          return false;
        }
      }
    }
    return true;
  }

  // Handle dine-in / walk-in aliases
  if (
    key === "permission_pos_walk_in_order" ||
    key === "permission_walk_in_order" ||
    key === "permission_dine_in"
  ) {
    const keys = ["permission_pos_walk_in_order", "permission_walk_in_order", "permission_dine_in"];
    for (const k of keys) {
      if (permissions[k] !== undefined && permissions[k] !== null) {
        const val = permissions[k];
        if (val === 0 || val === "0" || val === false) {
          return false;
        }
      }
    }
    return true;
  }

  // Handle e-menu aliases
  if (
    key === "permission_pos_e_menu" ||
    key === "pos_e_menu" ||
    key === "permission_e_menu" ||
    key === "emenu_login"
  ) {
    const keys = ["permission_pos_e_menu", "pos_e_menu", "permission_e_menu", "emenu_login"];
    for (const k of keys) {
      if (permissions[k] !== undefined && permissions[k] !== null) {
        const val = permissions[k];
        if (val === 0 || val === "0" || val === false) {
          return false;
        }
      }
    }
    return true;
  }

  // Handle chef panel / chef login aliases
  if (
    key === "permission_pos_chef_panel" ||
    key === "pos_chef_panel" ||
    key === "permission_chef_panel" ||
    key === "permission_chef_login" ||
    key === "chef_login"
  ) {
    const keys = [
      "permission_pos_chef_panel",
      "pos_chef_panel",
      "permission_chef_panel",
      "permission_chef_login",
      "chef_login",
    ];
    for (const k of keys) {
      if (permissions[k] !== undefined && permissions[k] !== null) {
        const val = permissions[k];
        if (val === 0 || val === "0" || val === false) {
          return false;
        }
      }
    }
    return true;
  }

  // Handle drive-thru aliases
  if (
    key === "permission_drive_thru" ||
    key === "drive_thru" ||
    key === "permission_drive_through" ||
    key === "drive_through"
  ) {
    const keys = ["permission_drive_thru", "drive_thru", "permission_drive_through", "drive_through"];
    for (const k of keys) {
      if (permissions[k] !== undefined && permissions[k] !== null) {
        const val = permissions[k];
        if (val === 0 || val === "0" || val === false) {
          return false;
        }
      }
    }
    return true;
  }

  // Handle take away aliases
  if (key === "permission_take_away" || key === "take_away") {
    const keys = ["permission_take_away", "take_away"];
    for (const k of keys) {
      if (permissions[k] !== undefined && permissions[k] !== null) {
        const val = permissions[k];
        if (val === 0 || val === "0" || val === false) {
          return false;
        }
      }
    }
    return true;
  }

  // Handle delivery aliases
  if (key === "permission_delivery" || key === "delivery") {
    const keys = ["permission_delivery", "delivery"];
    for (const k of keys) {
      if (permissions[k] !== undefined && permissions[k] !== null) {
        const val = permissions[k];
        if (val === 0 || val === "0" || val === false) {
          return false;
        }
      }
    }
    return true;
  }

  // Handle table booking aliases
  if (key === "permission_table_booking" || key === "table_booking") {
    const keys = ["permission_table_booking", "table_booking"];
    for (const k of keys) {
      if (permissions[k] !== undefined && permissions[k] !== null) {
        const val = permissions[k];
        if (val === 0 || val === "0" || val === false) {
          return false;
        }
      }
    }
    return true;
  }

  // Handle collection point aliases
  if (key === "permission_collection_point" || key === "collection_point") {
    const keys = ["permission_collection_point", "collection_point"];
    for (const k of keys) {
      if (permissions[k] !== undefined && permissions[k] !== null) {
        const val = permissions[k];
        if (val === 0 || val === "0" || val === false) {
          return false;
        }
      }
    }
    return true;
  }

  if (permissions[key] === undefined || permissions[key] === null) {
    return true;
  }
  const val = permissions[key];
  if (val === 0 || val === "0" || val === false) {
    return false;
  }
  return true;
};

export const PermissionRoute = ({ permissionKey, settingKey, redirectTo = "/" }) => {
  const posData = useSelector((state) => state?.posData);
  const token = posData?.token;
  const { data: settingsData } = useGetSettingsQuery(undefined, { skip: !token });
  const permissions = getOrderTypePermissions(posData);

  const allowed = isPermissionAllowed(permissions, permissionKey);

  if (!allowed) {
    return <Navigate to={redirectTo} replace />;
  }

  const settings = settingsData?.data?.settings;
  if (settings) {
    const keyToCheck =
      settingKey ||
      (permissionKey === "permission_pos_calender_view" || permissionKey === "permission_pos_calendar_view"
        ? "calendar"
        : permissionKey === "permission_pos_inventory"
        ? "inventory"
        : permissionKey === "permission_pos_chef_panel"
        ? "serve_panel"
        : null);

    if (
      keyToCheck &&
      settings[keyToCheck] !== undefined &&
      (settings[keyToCheck] === 0 || settings[keyToCheck] === "0" || settings[keyToCheck] === false)
    ) {
      return <Navigate to={redirectTo} replace />;
    }
  }

  return <Outlet />;
};

// export const ProtectedRoutes = () => {
//   const token = useSelector((state) => state?.posData?.token);
//   return token ? <Outlet /> : <Navigate to="/login" />;
// }

// export const ProtectedRoutesforLoggedIn = () => {
//   const token = useSelector((state) => state.posData?.token);
//   return !token ? <Outlet /> : <Navigate to="/" />
// }

export const emailRegex = new RegExp(
  /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
);


// TimeSlots for table booking
export const timeSlots = {
  Breakfast: ["09:00", "09:30", "10:00", "10:30", "11:00", "11:30"],
  Lunch: ["12:00", "12:30", "13:00", "13:30", "14:00", "14:30"],
  Dinner: ["18:00", "18:30", "19:00", "19:30", "20:00", "20:30"],
};

// Generate next 4 days for table booking
export const generateDays = () => {
  const days = [];
  const today = new Date();

  for (let i = 0; i < 5; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);

    const isToday = date.toDateString() === today.toDateString();

    const day = date.getDate();
    const month = date
      .toLocaleString("default", { month: "short" })
      .toUpperCase();
    const dayName = date
      .toLocaleString("default", { weekday: "short" })
      .toUpperCase();

    days.push({
      // if it's today, replace the value string with "Today"
      value: isToday ? "Today" : `${day} ${month}`,
      // if it's today, label it as "TODAY", otherwise use the weekday
      label: isToday ? "Today" : dayName,
      // you can still show the full date as a subLabel if you like:
      subLabel: `${day} ${month}`,
      date,
    });
  }


  return days;
};

// export function parseHourMinuteString(str) {
export const parseHourMinuteString = (str) => {
  if (!str) return null
  const today = dayjs().format('YYYY-MM-DD')
  const returnStr = dayjs(`${today} ${str}`, 'YYYY-MM-DD hh:mmA')
  return returnStr;
}


export function convertTo24Hour(timeStr) {
  const parsed = dayjs(timeStr, "hh:mmA");
  return parsed.isValid() ? parsed.format("HH:mm") : "Invalid Time";
}
/** Turn "12:30PM" or "00:15" → total minutes past midnight */
export function toMinutes(timeStr) {
  const [h, m] = convertTo24Hour(timeStr).split(":").map(Number);
  return h * 60 + m;
}



// 1) Parse “Today”, “Tomorrow”, or “21 JUN” → “YYYY‑MM‑DD” (UTC)
export function parseBookingDate(dayStr) {
  const today = new Date();
  // today’s UTC date
  const utcToday = new Date(Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate()
  ));
  if (!dayStr) return utcToday.toISOString().split("T")[0];

  const key = dayStr.trim().toLowerCase();
  if (key === "today") {
    return utcToday.toISOString().split("T")[0];
  }
  if (key === "tomorrow") {
    const t = new Date(utcToday);
    t.setUTCDate(t.getUTCDate() + 1);
    return t.toISOString().split("T")[0];
  }

  // Expecting e.g. "21 JUN" or "5 Feb"
  const [d, m] = dayStr.trim().split(" ");
  const monthNames = {
    JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5,
    JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11
  };
  const monIndex = monthNames[m.toUpperCase()];
  if (monIndex == null || isNaN(+d)) {
    console.warn("Unexpected booking date format:", dayStr);
    return utcToday.toISOString().split("T")[0];
  }
  // Build a UTC date for current year
  const dt = new Date(Date.UTC(today.getUTCFullYear(), monIndex, parseInt(d, 10)));
  return dt.toISOString().split("T")[0];
}

// 2) Parse “09:00AM” / “9:30 PM” → “HH:MM:SS”
export function parseBookingTime(timeStr) {
  if (!timeStr) return "00:00:00";
  const m = timeStr.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) {
    console.warn("Unexpected time format:", timeStr);
    return "00:00:00";
  }
  let hour = parseInt(m[1], 10);
  const min = m[2];
  const ampm = m[3].toUpperCase();

  if (ampm === "PM" && hour < 12)   hour += 12;
  if (ampm === "AM" && hour === 12) hour = 0;
  const hh = hour.toString().padStart(2, "0");
  return `${hh}:${min}:00`;
}


export // 0) helper to combine & spit back UTC date/time
  function getUTCDateTime(dayStr, timeStr) {
  // 1) figure out the YYYY-MM-DD in UTC
  const datePart = parseBookingDate(dayStr);

  // 2) figure out HH:MM:SS local
  const [hh, mm, ss] = parseBookingTime(timeStr).split(':').map(Number);

  // 3) build a local‐zone Date
  const [Y, M, D] = datePart.split('-').map(Number);
  const localDT = new Date(Y, M - 1, D, hh, mm, ss);

  // 4) extract UTC bits
  const iso = localDT.toISOString();         // e.g. "2025-07-14T04:30:00.000Z"
  const [utcDate, utcTimeWithMs] = iso.split('T');
  const utcTime = utcTimeWithMs.split('.')[0]; // "04:30:00"

  return { utcDate, utcTime };
}


/** Turn local "HH:MM:SS" -> UTC "HH:MM:SS" for a specific day */
export function getUTCFromLocalTime(dayStr, localTimeStr) {
  if (!localTimeStr) return "00:00:00";
  const datePart = parseBookingDate(dayStr);
  const [hh, mm, ss] = localTimeStr.split(':').map(Number);
  const [Y, M, D] = datePart.split('-').map(Number);
  const localDT = new Date(Y, M - 1, D, hh, mm, ss);
  const iso = localDT.toISOString();
  return iso.split('T')[1].split('.')[0];
}


export function utcTimeToLocal(timeStr, dateStr) {
  const [h, m, s] = timeStr?.split(':')?.map(Number);

  // Anchor date: either the provided one, or today
  let year, month, day;
  if (dateStr) {
    [year, month, day] = dateStr?.split('-')?.map(Number);
  } else {
    const now = new Date();
    year   = now.getFullYear();
    month  = now.getMonth() + 1; // JS month is 0‑11
    day    = now.getDate();
  }

  // Build a UTC Date from those components
  const utcDate = new Date(Date.UTC(year, month - 1, day, h, m, s));

  // Now read out local hours/minutes
  const localH = utcDate.getHours();
  const localM = utcDate.getMinutes();

  // Format back to H:MM (pad minutes to two digits)
  return `${localH}:${localM.toString().padStart(2, '0')}`;
}

export function parseUtcToLocalDate(dateStr, timeStr) {
  // NOTE the uppercase “Z” suffix!
  return new Date(`${dateStr}T${timeStr}Z`);
}

export function getAllISOFromDialCode(code) {
  return dialCodeToISO[code] || [];
}


// formatCurrency.js
const CURRENCY_MAP = {
  EURO: 'EUR',
  USD:  'USD',
  INR:  'INR',
  GBP:  'GBP',
  JPY:  'JPY',
};

export function formatCurrency(
  amount,
  backendCurrencyCode,
  locale = undefined
) {
  // 1. Normalize the code:
  const isoCode = CURRENCY_MAP[backendCurrencyCode] || backendCurrencyCode;

  // 2. Pick a locale:
  //    – If locale was passed in explicitly, use it.
  //    – Otherwise fall back to the browser’s default.
  const fmtLocale = locale || navigator.language;

  // 3. Format:
  return new Intl.NumberFormat(fmtLocale, {
    style:               'currency',
    currency:            isoCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function getCurrencySymbol(backendCurrencyCode) {
  const symbolMap = {
    EURO: '€',
    USD: '$',
    INR: '₹',
    GBP: '£',
    JPY: '¥',
  };
  return symbolMap[backendCurrencyCode] || backendCurrencyCode;
}

export const formatDate = (date) => {
  const format = new Date(date);
  const formatDate = format?.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  return formatDate;
}

export const formattedDateTime = (date) => {
  const orderDate = new Date(date);
  const formatDate = orderDate?.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });

  const formatTime = orderDate?.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit'
  });
  return `${formatDate}, ${formatTime}`;

}


// utils.js
export const groupByStatus = (orders) => ({
  ready_to_prepare: orders.filter(o => o.status === 'ready_to_prepare').map(o => o.id),
  preparing:        orders.filter(o => o.status === 'preparing').map(o => o.id),
  ready_to_serve:   orders.filter(o => o.status === 'ready_to_serve').map(o => o.id),
  served:           orders.filter(o => o.status === 'served').map(o => o.id),
});



export function formatDateTime(isoString) {
  if (!isoString) return ""; // handle null/undefined

  let date;
  try {
    // If it's already a Date, keep it, else parse string
    date = isoString instanceof Date ? isoString : parseISO(isoString);
    if (isNaN(date)) return ""; // invalid date
  } catch (e) {
    return "";
  }

  if (isToday(date)) {
    return `Today | ${format(date, "hh:mm a")}`;
  } else if (isYesterday(date)) {
    return `Yesterday | ${format(date, "hh:mm a")}`;
  } else {
    return `${format(date, "MMM dd")} | ${format(date, "hh:mm a")}`;
  }
}


export const calculateDiscount = (cartTotal, discountType, discountValue) => {
  if (!discountType || !discountValue) return 0;
  if (discountType === "percent") {
    return (parseFloat(cartTotal) * parseFloat(discountValue)) / 100;
  } else if (discountType === "fixed") {
    return parseFloat(discountValue) > parseFloat(cartTotal) ? parseFloat(cartTotal) : parseFloat(discountValue); // avoid negative totals
  }
  return 0;
};

/**
 * Checks if a given time value is within the vendor's operating hours.
 * @param {dayjs.Dayjs} timeValue - The time to check.
 * @param {Array} vendorTimings - Array of vendor timing objects.
 * @param {boolean|null} isWeekendOnly - Filter for specific days. If null, checks all open days.
 * @returns {boolean} - True if valid, false otherwise.
 */
export const isTimeValidForVendor = (timeValue, vendorTimings, isWeekendOnly) => {
  if (!vendorTimings || !timeValue) return true;
  const hour = timeValue.hour();
  const minute = timeValue.minute();
  const timeStr = `${hour.toString().padStart(2, "0")}:${minute
    .toString()
    .padStart(2, "0")}`;

  let relevantDays;
  if (isWeekendOnly === true) {
    relevantDays = [0, 6]; // Sunday, Saturday
  } else if (isWeekendOnly === false) {
    relevantDays = [1, 2, 3, 4, 5]; // Mon-Fri
  } else {
    relevantDays = [0, 1, 2, 3, 4, 5, 6];
  }

  const openDays = vendorTimings.filter(
    (t) => t.is_open && relevantDays.includes(t.day_of_week)
  );

  if (openDays.length === 0) return false;

  // Time is valid only if it falls within at least one slot for EVERY relevant open day
  return openDays.every((day) => {
    return day.slots.some((slot) => {
      const open = slot.open_time.substring(0, 5);
      const close = slot.close_time.substring(0, 5);
      return timeStr >= open && timeStr < close;
    });
  });
};

/**
 * Determines whether an order type filter should be shown based on order_type_flags.
 * @param {string} filterId - The ID of the filter (e.g. 'All', 'pos', 'collection_point', 'dine_in', 'delivery', 'phone_order', 'booking')
 * @param {Object} flags - The order_type_flags object from API state/stats
 * @returns {boolean} - True if allowed/visible, false if disabled/hidden.
 */
export const isOrderTypeFlagAllowed = (filterId, flags) => {
  if (!flags || typeof flags !== 'object' || Object.keys(flags).length === 0) {
    return true; // default to showing all if flags are not yet loaded or not provided
  }

  const check = (val) => {
    if (val === undefined || val === null) return undefined;
    return val === true || val === 1 || val === "1" || val === "true";
  };

  switch (filterId) {
    case 'All':
    case 'all': {
      const v = check(flags.all ?? flags.All);
      return v !== undefined ? v : true;
    }
    case 'pos':
    case 'take_away':
    case 'takeaway': {
      const v = check(flags.take_away ?? flags.pos ?? flags.takeaway);
      return v !== undefined ? v : true;
    }
    case 'collection_point': {
      const v = check(flags.collection_point);
      return v !== undefined ? v : true;
    }
    case 'dine_in': {
      const v = check(flags.dine_in ?? flags.walk_in);
      return v !== undefined ? v : true;
    }
    case 'delivery': {
      const v = check(flags.delivery);
      return v !== undefined ? v : true;
    }
    case 'phone_order': {
      const v = check(flags.phone_order);
      return v !== undefined ? v : true;
    }
    case 'booking':
    case 'table_booking':
    case 'scheduled': {
      const v = check(flags.booking ?? flags.table_booking ?? flags.scheduled);
      return v !== undefined ? v : true;
    }
    default: {
      const v = check(flags[filterId]);
      return v !== undefined ? v : true;
    }
  }
};

/**
 * Resolves a full, valid URL for QR code images or file paths.
 * Supports absolute URLs (http/https), /api/ relative paths, /files/ relative paths, and bare filenames.
 *
 * @param {string} qrPath The relative or absolute path of the QR code image
 * @returns {string} The complete URL
 */
export const getFullQrUrl = (qrPath) => {
  if (!qrPath || typeof qrPath !== 'string') return '';
  if (
    qrPath.startsWith('http://') ||
    qrPath.startsWith('https://') ||
    qrPath.startsWith('blob:') ||
    qrPath.startsWith('data:')
  ) {
    return qrPath;
  }
  const apiRoute = (process.env.REACT_APP_API_ROUTE || 'https://serve-link.app/api').replace(/\/+$/, '');
  if (qrPath.startsWith('/api/')) {
    const baseOrigin = apiRoute.replace(/\/api\/?$/, '');
    return `${baseOrigin}${qrPath}`;
  }
  if (qrPath.startsWith('/')) {
    return `${apiRoute}${qrPath}`;
  }
  return `${apiRoute}/files/${qrPath}`;
};

